/**
 * Service Factory and Dependency Injection Container
 * Manages service instances and their dependencies
 */

import { createLogger } from "@/utils/logger";

import { BaseService } from "./base-service";
import { cacheManager } from "./cache";
import { ServiceFactory, ServiceConfig, IService } from "./types";

const logger = createLogger("service-factory");

/**
 * Concrete service factory implementation
 */
export class UnifiedServiceFactory implements ServiceFactory {
  private static instance: UnifiedServiceFactory;
  private services = new Map<string, IService>();
  private constructors = new Map<
    string,
    new (config: ServiceConfig) => IService
  >();

  private constructor() {
    logger.info("Service factory initialized");
  }

  /**
   * Get singleton instance
   */
  static getInstance(): UnifiedServiceFactory {
    if (!UnifiedServiceFactory.instance) {
      UnifiedServiceFactory.instance = new UnifiedServiceFactory();
    }
    return UnifiedServiceFactory.instance;
  }

  /**
   * Register a service constructor
   */
  registerConstructor<T extends IService>(
    name: string,
    constructor: new (config: ServiceConfig) => T,
  ): void {
    this.constructors.set(name, constructor);
    logger.debug("Service constructor registered", { name });
  }

  /**
   * Create service instance with configuration
   */
  create<T extends IService>(config: ServiceConfig): T {
    const Constructor = this.constructors.get(config.name);

    if (!Constructor) {
      throw new Error(`No constructor registered for service: ${config.name}`);
    }

    const service = new Constructor(config) as T;
    this.register(service);

    logger.info("Service created", {
      name: config.name,
      type: Constructor.name,
      config: this.sanitizeConfig(config),
    });

    return service;
  }

  /**
   * Register existing service instance
   */
  register<T extends IService>(service: T): void {
    this.services.set(service.name, service);
    logger.debug("Service registered", { name: service.name });
  }

  /**
   * Get service instance by name
   */
  get<T extends IService>(name: string): T | undefined {
    return this.services.get(name) as T;
  }

  /**
   * Get service instance by name with type assertion
   */
  getRequired<T extends IService>(name: string): T {
    const service = this.get<T>(name);
    if (!service) {
      throw new Error(`Service not found: ${name}`);
    }
    return service;
  }

  /**
   * List all registered service names
   */
  list(): string[] {
    return Array.from(this.services.keys());
  }

  /**
   * Check if service exists
   */
  has(name: string): boolean {
    return this.services.has(name);
  }

  /**
   * Remove service from registry
   */
  unregister(name: string): boolean {
    const service = this.services.get(name);
    if (
      service &&
      "destroy" in service &&
      typeof service.destroy === "function"
    ) {
      (service as any).destroy();
    }

    const removed = this.services.delete(name);
    if (removed) {
      logger.info("Service unregistered", { name });
    }
    return removed;
  }

  /**
   * Health check all registered services
   */
  async healthCheckAll(): Promise<Record<string, any>> {
    const results: Record<string, any> = {};
    const promises: Promise<void>[] = [];

    for (const [name, service] of this.services) {
      promises.push(
        service
          .healthCheck()
          .then((result) => {
            results[name] = result;
          })
          .catch((error) => {
            results[name] = {
              status: "unhealthy",
              error: error instanceof Error ? error.message : "Unknown error",
              timestamp: new Date().toISOString(),
            };
          }),
      );
    }

    await Promise.all(promises);

    logger.debug("Health check completed for all services", {
      totalServices: this.services.size,
      results: Object.keys(results),
    });

    return results;
  }

  /**
   * Get metrics from all services
   */
  async getAllMetrics(): Promise<Record<string, any>> {
    const metrics: Record<string, any> = {};
    const promises: Promise<void>[] = [];

    for (const [name, service] of this.services) {
      promises.push(
        service
          .getMetrics()
          .then((result) => {
            metrics[name] = result;
          })
          .catch((error) => {
            metrics[name] = {
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to get metrics",
              timestamp: new Date().toISOString(),
            };
          }),
      );
    }

    await Promise.all(promises);
    return metrics;
  }

  /**
   * Clear cache for all services or specific pattern
   */
  async clearAllCaches(pattern?: string): Promise<Record<string, number>> {
    const results: Record<string, number> = {};
    const promises: Promise<void>[] = [];

    for (const [name, service] of this.services) {
      promises.push(
        service
          .clearCache(pattern)
          .then(() => {
            // Get cache stats to see how many entries were cleared
            const stats = cacheManager.getAllStats()[name];
            results[name] = stats ? stats.deletes : 0;
          })
          .catch((error) => {
            logger.warn(`Failed to clear cache for service ${name}`, { error });
            results[name] = 0;
          }),
      );
    }

    await Promise.all(promises);

    logger.info("Cache cleared for all services", { pattern, results });
    return results;
  }

  /**
   * Gracefully shutdown all services
   */
  async shutdown(): Promise<void> {
    logger.info("Shutting down all services", { count: this.services.size });

    const shutdownPromises: Promise<void>[] = [];

    for (const [name, service] of this.services) {
      shutdownPromises.push(
        new Promise<void>((resolve) => {
          try {
            if ("destroy" in service && typeof service.destroy === "function") {
              (service as any).destroy();
            }
            logger.debug("Service shut down", { name });
          } catch (error) {
            logger.warn("Error during service shutdown", { name, error });
          } finally {
            resolve();
          }
        }),
      );
    }

    await Promise.all(shutdownPromises);
    this.services.clear();
    this.constructors.clear();

    // Cleanup cache manager
    cacheManager.destroy();

    logger.info("All services shut down successfully");
  }

  /**
   * Get factory statistics
   */
  getStats(): {
    totalServices: number;
    registeredConstructors: number;
    services: { name: string; type: string }[];
  } {
    return {
      totalServices: this.services.size,
      registeredConstructors: this.constructors.size,
      services: Array.from(this.services.entries()).map(([name, service]) => ({
        name,
        type: service.constructor.name,
      })),
    };
  }

  /**
   * Sanitize config for logging
   */
  private sanitizeConfig(config: ServiceConfig): Partial<ServiceConfig> {
    const { headers, ...safeConfig } = config;
    return {
      ...safeConfig,
      headers: headers ? Object.keys(headers) : undefined,
    };
  }
}

/**
 * Service builder for fluent configuration
 */
export class ServiceBuilder {
  private config: Partial<ServiceConfig> = {};

  constructor(name: string) {
    this.config.name = name;
  }

  baseURL(url: string): ServiceBuilder {
    this.config.baseURL = url;
    return this;
  }

  timeout(ms: number): ServiceBuilder {
    this.config.timeout = ms;
    return this;
  }

  retries(count: number): ServiceBuilder {
    this.config.retries = count;
    return this;
  }

  retryDelay(ms: number): ServiceBuilder {
    this.config.retryDelay = ms;
    return this;
  }

  caching(
    enabled: boolean,
    defaultTtl?: number,
    strategy?: "cache-first" | "network-first",
  ): ServiceBuilder {
    this.config.cache = {
      enabled,
      defaultTtl: defaultTtl || 5 * 60 * 1000,
      strategy: strategy || "cache-first",
    };
    return this;
  }

  logging(enabled: boolean): ServiceBuilder {
    this.config.enableLogging = enabled;
    return this;
  }

  metrics(enabled: boolean): ServiceBuilder {
    this.config.enableMetrics = enabled;
    return this;
  }

  headers(headers: Record<string, string>): ServiceBuilder {
    this.config.headers = headers;
    return this;
  }

  credentials(credentials: RequestCredentials): ServiceBuilder {
    this.config.credentials = credentials;
    return this;
  }

  build<T extends IService>(): ServiceConfig {
    if (!this.config.name) {
      throw new Error("Service name is required");
    }

    // Set defaults
    const config: ServiceConfig = {
      name: this.config.name,
      baseURL:
        this.config.baseURL ||
        process.env.NEXT_PUBLIC_BASE_URL ||
        "http://localhost:3000",
      timeout: this.config.timeout || (
        process.env.NODE_ENV === "development" || process.env.NEXT_PUBLIC_ENV === "development"
          ? parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT || process.env.API_TIMEOUT || "1500") || 1500
          : 30000
      ),
      retries: this.config.retries || 3,
      retryDelay: this.config.retryDelay || 1000,
      enableLogging: this.config.enableLogging ?? true,
      enableMetrics: this.config.enableMetrics ?? true,
      cache: this.config.cache || {
        enabled: false,
        defaultTtl: 5 * 60 * 1000,
        strategy: "cache-first",
      },
      headers: this.config.headers || { "Content-Type": "application/json" },
      credentials: this.config.credentials || "include",
    };

    return config;
  }
}

// Global factory instance
export const serviceFactory = UnifiedServiceFactory.getInstance();

/**
 * Convenience function for creating service configurations
 */
export function createServiceConfig(name: string): ServiceBuilder {
  return new ServiceBuilder(name);
}

/**
 * Register default service types
 */
export function registerDefaultServices(): void {
  // Register base service constructor
  serviceFactory.registerConstructor("base", BaseService as any);

  logger.info("Default services registered");
}

// Auto-register default services
registerDefaultServices();
